import React from "react";
import sortBy from "lodash/sortBy";
import { useSearchParams } from "react-router-dom";
import { useStaticData } from "context/staticData";
import SelectDropdown from "shared/form/core/selectDropdown";
import { SelectFilterOutputConfig, SelectFilterProps } from "./types";
import { MULTIPLE_VALUES_IN_URL_SEPARATOR } from "shared/utils/constants";
import { getSearchFilterValue } from "./utils";

/**
 * @description Use this hook for showing top filters. For example check registration organization,
 * @param SelectFilterProps
 * @returns SelectFilterOutputConfig
 */
export default function useSelectFilter({ isClearable = true, ...props }: SelectFilterProps): SelectFilterOutputConfig {
  const [searchParams, setSearchParams] = useSearchParams();
  const staticDataObj = useStaticData();

  const filterOptions = React.useMemo(() => {
    // if options array is passed
    if (props.options) {
      return props.options;
    }
    // if static data key is passed then generate options array
    if (props.optionKey) {
      const filterObject = staticDataObj[props.optionKey] || {};
      return sortBy(
        Object.keys(filterObject).map(key => ({
          label: filterObject[key],
          value: key
        })),
        ({ label }) => label.toLowerCase()
      );
    }

    return [];
  }, [props.optionKey, props.options, staticDataObj]);

  const selectedValue = React.useMemo(
    () =>
      getSearchFilterValue({
        name: props.name,
        searchParams,
        isMulti: props.isMulti
      }),
    [props.isMulti, props.name, searchParams]
  );

  const selectedOption = React.useMemo(() => {
    if (!selectedValue) {
      return null;
    }
    if (props.isMulti) {
      return filterOptions.filter(option => selectedValue.includes(option.value));
    }
    return filterOptions.find(option => option.value === selectedValue);
  }, [props.isMulti, filterOptions, selectedValue]);

  const filterChangeHandler = React.useCallback(
    (selectedOption: any, meta: any) => {
      if (selectedOption && props.isMulti) {
        if (!selectedOption.length) {
          // this is done so that for multi-select filters, filter key should get cleared from search params in case of clear
          searchParams.delete(meta.name);
          setSearchParams(searchParams);
        } else {
          setSearchParams({
            ...Object.fromEntries(searchParams),
            [meta.name]: selectedOption.map((option: any) => option.value).join(MULTIPLE_VALUES_IN_URL_SEPARATOR)
          });
        }
      } else if (selectedOption) {
        setSearchParams({
          ...Object.fromEntries(searchParams),
          [meta.name]: selectedOption.value
        });
      } else {
        searchParams.delete(meta.name);
        setSearchParams(searchParams);
      }

      if (!!props.changeHandler) {
        props.changeHandler?.(
          props.isMulti ? selectedOption?.map((option: any) => option.value) || [] : selectedOption?.value || null
        );
      }
    },
    [props, searchParams, setSearchParams]
  );

  const reset = React.useCallback(() => {
    searchParams.delete(props.name);
    setSearchParams(searchParams);
  }, [props.name, searchParams, setSearchParams]);

  const filterComponent = React.useMemo(() => {
    return filterOptions.length ? (
      <SelectDropdown
        isClearable={isClearable}
        isMulti={props.isMulti}
        name={props.name}
        placeholder={props.placeholder}
        options={filterOptions}
        value={selectedOption}
        onChange={filterChangeHandler}
      />
    ) : null;
  }, [filterChangeHandler, isClearable, props.isMulti, props.name, filterOptions, props.placeholder, selectedOption]);

  return {
    selectedValue,
    filterComponent,
    reset
  };
}
